import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { beUserInfo } from 'actions/fetchUserInfo.action';
import buildCapitalSessionPayloads from 'bl/capital/buildCapitalSessionPayloads.bl';
import STRIPE_STATUS from 'constants/stripeStatus.const';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import { selectAccessSalonId, selectAccessStylistId, selectAccessUserId } from 'selectors/access.selectors';
import { selectCurrentSalon, selectCurrentSalonStripeStatus } from 'selectors/currentSalon.selectors';
import { isEffectiveManager, isEffectiveReceptionist } from 'selectors/user';

export const useStripeCapitalPayloads = () => {
  const currentSalon = useSelector(selectCurrentSalon);
  const stripeStatus = useSelector(selectCurrentSalonStripeStatus);
  const accessSalonId = useSelector(selectAccessSalonId);
  const stylistId = useSelector(selectAccessStylistId);
  const userId = useSelector(selectAccessUserId);
  const manager = useSelector(isEffectiveManager);
  const receptionist = useSelector(isEffectiveReceptionist);

  const salonId = currentSalon?.id ?? accessSalonId ?? null;

  const { qj, isLoading, isError } = usePrepareReadOnly(beUserInfo);

  const stripeAccountId = qj?.stripe_account_id ?? null;
  const hasFetched = !isLoading;

  const salonHasStripe = !!currentSalon?.has_ncob_stripe_account || (!!stripeStatus && stripeStatus !== STRIPE_STATUS.noAccount);

  const hasStripeRelationship = hasFetched ? !!stripeAccountId || (isError && salonHasStripe) : false;

  const payloads = useMemo(() => {
    if (!hasFetched) return [];

    // Prefer beUserInfo ids: the query is keyed by salonId, so qj always matches the current salon.
    // Redux access selectors can momentarily hold the previous salon's ids on switch/impersonation;
    // use them only as a fallback when qj has not provided a value.
    const effectiveStylistId = qj?.stylist_id ?? stylistId ?? null;
    const effectiveUserId = qj?.user_id ?? userId ?? null;

    // Role selectors (manager/receptionist) can also lag after SPA navigation, leaving Capital silent
    // for managers/owners without a stylist row. When beUserInfo has authorised the user for this salon
    // (stripe_account_id present) and they are not flagged as a receptionist, allow the business payload
    // to be attempted; the BE remains the final authority and returns 404 / 204 for ineligible users.
    const hasAuthorisedAccess = !!qj?.stripe_account_id;
    const effectiveIsManager = manager || (hasAuthorisedAccess && !receptionist);

    return buildCapitalSessionPayloads({
      salonId,
      stylistId: effectiveStylistId,
      userId: effectiveUserId,
      isManager: effectiveIsManager,
      isReceptionist: receptionist,
    });
  }, [salonId, stylistId, userId, manager, receptionist, hasFetched, qj?.stripe_account_id, qj?.stylist_id, qj?.user_id]);

  return { payloads, salonId, merchantId: stripeAccountId, hasStripeRelationship };
};
